import { EPermissionGrantActions, EPermissionGrantSubjects, TPermissionGrant } from './permissions.type'

export function isDisabledGrantSubjectAction(
  subject: EPermissionGrantSubjects,
  action: EPermissionGrantActions,
) {
  const disabledGrantSubjectActions: TPermissionGrant[] = [
    {
      subject: EPermissionGrantSubjects.PATIENT_REVIEWS,
      actions: [
        EPermissionGrantActions.CREATE,
        EPermissionGrantActions.DELETE,
        EPermissionGrantActions.UPDATE,
        EPermissionGrantActions.ALL,
      ],
    },
    {
      subject: EPermissionGrantSubjects.STATISTICS,
      actions: [
        EPermissionGrantActions.CREATE,
        EPermissionGrantActions.DELETE,
        EPermissionGrantActions.UPDATE,
        EPermissionGrantActions.ALL,
      ],
    },
  ]

  return (
    disabledGrantSubjectActions?.find((grant) => grant.subject === subject)?.actions?.includes(action) ||
    false
  )
}

export function getAvailableActionsForSubject(subject: EPermissionGrantSubjects): EPermissionGrantActions[] {
  const availableActions = Object.values(EPermissionGrantActions).filter(
    (action) => !isDisabledGrantSubjectAction(subject, action),
  )
  return availableActions
}

/**
 * When a permission grants ALL + others actions, the other actions are superfluous
 * This functions removes them
 * @returns {Array<TPermissionGrant>}
 * @param allGrants {Array<TPermissionGrant>}
 */
export function shortenGrantActions(allGrants: Array<TPermissionGrant>): Array<TPermissionGrant> {
  const subjectAll: TPermissionGrant = allGrants?.find((t) => t.subject === EPermissionGrantSubjects.ALL) ?? {
    subject: EPermissionGrantSubjects.ALL,
    actions: [],
  }
  if (subjectAll.actions.includes(EPermissionGrantActions.ALL)) {
    return [{ subject: EPermissionGrantSubjects.ALL, actions: [EPermissionGrantActions.ALL] }]
  }

  const result: TPermissionGrant[] = []

  for (const grant of allGrants) {
    if (grant.actions.includes(EPermissionGrantActions.ALL)) {
      grant.actions = [EPermissionGrantActions.ALL]
    }
    if (grant.subject !== EPermissionGrantSubjects.ALL) {
      // On each grant, only keep actions that are not already granted in the subject `ALL`
      grant.actions = grant.actions.filter((a) => !subjectAll.actions.includes(a))
    }

    result.push(grant)
  }

  return result.filter((g) => g.actions?.length > 0)
}

/**
 * Adds all superfluous actions when a grant gives `ALL` action
 * This is useful for clearly displaying all actions as granted when the ALL action
 * is granted in the interface
 * @returns {TPermissionGrant[]}
 * @param allGrants {TPermissionGrant[]}
 */
export function expandGrantActions(allGrants: TPermissionGrant[]): TPermissionGrant[] {
  const subjectAll = allGrants?.find((t) => t.subject === EPermissionGrantSubjects.ALL) ?? { actions: [] }
  const result = []
  for (const subject of Object.values(EPermissionGrantSubjects)) {
    const availableActionsForSubject = getAvailableActionsForSubject(subject)
    const existingGrantOnSubject = allGrants?.find((t) => t.subject === subject)
    let actions = existingGrantOnSubject?.actions ?? []
    actions.push(...(subjectAll.actions || [])) // Inherit actions from subject ALL
    if (actions.includes(EPermissionGrantActions.ALL)) {
      actions = availableActionsForSubject
    } else if (subjectAll.actions.length > 0) {
      actions.push(...subjectAll.actions) // Add existing actions
    }

    // Remove duplicates and ensure actions are within availableActions for the subject
    actions = [...new Set(actions.filter((action) => availableActionsForSubject.includes(action)))]

    result.push({
      subject,
      actions,
    })
  }

  return result
}

function removePermissionGrantAction(
  action: EPermissionGrantActions,
  existingGrant: TPermissionGrant,
  subject: EPermissionGrantSubjects,
  resultGrants: TPermissionGrant[],
  recursionCount: number,
) {
  // rule 2: If the action `ALL` is removed, empty all actions on the subject
  if (action === EPermissionGrantActions.ALL) {
    existingGrant.actions = []
  } else {
    // rule 3: When any action is removed, remove the action `ALL` from the actions on the subject and from the subject ALL
    existingGrant.actions = existingGrant.actions.filter(
      (a) => a !== action && a !== EPermissionGrantActions.ALL,
    )
  }
  // Automatically remove all actions from subject ALL
  if (subject !== EPermissionGrantSubjects.ALL) {
    updatePermissionGrants({
      allGrants: resultGrants,
      operation: 'REMOVE',
      subject: EPermissionGrantSubjects.ALL,
      action: EPermissionGrantActions.ALL,
      recursionCount: recursionCount + 1,
    })
  }
}

function addPermissionGrantAction(
  action: EPermissionGrantActions,
  existingGrant: TPermissionGrant,
  availableActions: Array<EPermissionGrantActions>,
  resultGrants: Array<TPermissionGrant>,
  subject: EPermissionGrantSubjects,
) {
  // rule 1: If the action `ALL` is added, add all availableActions to the subject
  if (action === EPermissionGrantActions.ALL) {
    existingGrant.actions = [...availableActions]
  } else {
    if (existingGrant) {
      existingGrant.actions.push(action)
    } else {
      resultGrants.push({ subject, actions: [action] })
    }
    // rule 4: When any action is added, if the actions on the subject contains all availableActions except `ALL`, add `ALL`
    // Only do this if `ALL` is an available action
    if (!availableActions.includes(EPermissionGrantActions.ALL)) {
      return
    }
    const hasAllActions = availableActions
      .filter((a) => a !== EPermissionGrantActions.ALL)
      .every((avAction) => existingGrant && existingGrant.actions.includes(avAction))
    if (hasAllActions && !existingGrant.actions.includes(EPermissionGrantActions.ALL)) {
      existingGrant.actions.push(EPermissionGrantActions.ALL)
    }
  }
}

export function updatePermissionGrants({
  allGrants,
  operation,
  subject,
  action,
  recursionCount = 0,
}: {
  allGrants: Array<TPermissionGrant>
  operation: 'ADD' | 'REMOVE'
  subject: EPermissionGrantSubjects
  action: EPermissionGrantActions | undefined
  recursionCount?: number
}): TPermissionGrant[] {
  if (!action) {
    return allGrants
  }
  const availableActions = getAvailableActionsForSubject(subject)
  let resultGrants = [...allGrants]
  let existingGrant = resultGrants.find((grant) => grant.subject === subject)
  // If there's no existing grant, add it.
  if (!existingGrant) {
    existingGrant = { subject, actions: [] }
    resultGrants.push(existingGrant)
  }
  if (operation === 'ADD') {
    addPermissionGrantAction(action, existingGrant, availableActions, resultGrants, subject)
  } else if (operation === 'REMOVE') {
    removePermissionGrantAction(action, existingGrant, subject, resultGrants, recursionCount)
  }
  // rule 5: Special-case: When the subject changed is `ALL`, replicate the changes on each subject by calling the mutatePermissionGrants for each subject
  // Also avoids infinite recursions
  if (recursionCount === 0 && subject === EPermissionGrantSubjects.ALL) {
    Object.values(EPermissionGrantSubjects)
      .filter((s) => s !== EPermissionGrantSubjects.ALL)
      .forEach((s) => {
        resultGrants = updatePermissionGrants({ allGrants: resultGrants, operation, subject: s, action })
      })
  }
  return resultGrants
}

export function getValidIpList(inputValue: string) {
  const cleanedInputValue = inputValue.replace(/\s+/g, '')
  const ips = cleanedInputValue.split(',')

  const ipRegex = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/

  if (ips.every((ip) => ipRegex.test(ip))) {
    return ips
  }

  return null
}
